import { CardItem, OPItem } from "@/interfaces/CardItem";
import { getRoutePrefix } from "@/games";
import { SupportedGames } from "@/types/game";

import { Series } from "@/lib/urlHelper";

/**
 * Unicode dashes: hyphen, non-breaking hyphen, figure dash, en dash, em dash,
 * horizontal bar and the minus sign. Palworld names all read "Pal \u2013 Title",
 * and an en dash left alone becomes %E2%80%93 in the url -- unreadable, and the
 * name segment is there to be read. No One Piece name contains one, so folding
 * them to a plain hyphen moves no existing url.
 */
const DASH_LIKE = /[\u2010-\u2015\u2212]/g;

export function formatNameForUrl(name: string) {
  return (
    name
      .replace(/['"\u2018\u2019\u201C\u201D`]/g, "") // Remove all apostrophes and quotes (including Unicode)
      .replace(DASH_LIKE, "-")
      .replaceAll(" ", "-")
      .replaceAll(".", "-")
      .replaceAll("(", "")
      .replaceAll(")", "")
      .replaceAll("&", "and")
      .replaceAll("!", "")
      // Collapse any run of hyphens the substitutions above left behind. This
      // replaces a "---" then "--" pass, which missed longer runs.
      .replace(/-{2,}/g, "-")
      .replace(/^-+|-+$/g, "")
      .toLowerCase()
  );
}

export function getSetName(series: Series[], groupId: string) {
  for (const s of series) {
    if (s["name"] === groupId) {
      return s["setName"];
    }
  }
  return "";
}

export function getCardLink(cardData: OPItem) {
  return `/cards/${cardData.groupId}/${cardData.id}/${formatNameForUrl(cardData.name)}`;
}

export function getCardLink2(cardData: OPItem, tcgplayerId?: string) {
  if (tcgplayerId) {
    return `/card/${cardData.number}/${formatNameForUrl(cardData.name)}?src=tcgplayer&id=${tcgplayerId}`;
  }
  if (cardData.region) {
    return `/card/${cardData.number}/${formatNameForUrl(cardData.name)}?src=${cardData.region}&id=${cardData.id}`;
  }
  return `/card/${cardData.number}/${formatNameForUrl(cardData.name)}`;
}

export function getCardLinkCanonical(cardData: OPItem) {
  return `/card/${cardData.number}/${formatNameForUrl(cardData.name)}`;
}

/**
 * A card's detail page, for any game.
 *
 * One Piece owns the un-prefixed /card/... route on gumgum.gg and identifies
 * cards by `number` with the printing in the query string, so it keeps
 * getCardLink2. Every other game is prefixed and has one printing per card id,
 * so the id alone addresses the page.
 */
export function getGameCardLink(
  game: SupportedGames,
  card: CardItem,
): string | undefined {
  if (game === "one-piece") {
    return getCardLink2(card as OPItem);
  }

  if (game === "palworld") {
    return `/${getRoutePrefix(game)}card/${card.id}/${formatNameForUrl(card.name)}`;
  }

  // No detail page yet; the grid renders the card without a dead link.
  return undefined;
}

export function getGroupLink(series: Series[], seriesName: string) {
  return `/cards/${seriesName}/${formatNameForUrl(getSetName(series, seriesName))}`;
}
